// ============================================================
// ZONES - areas without volume.
//
// Some things a battlefield needs are not objects. Burning promethium,
// a mud flat, a minefield, the far side of a chasm: they are properties
// of GROUND, and giving them a height so they could be built out of
// cubes would be a lie that cover and line of sight would then believe.
//
// So a zone is a painted footprint with no height worth speaking of. It
// says what crossing it costs and whether it hurts, and nothing else.
//
// The implementation is deliberately dull: zones mesh into very flat
// solids with cover explicitly set to none. `terrainAt` probes just above
// the ground and finds them; the cover line never dips low enough to
// cross one. ZERO ENGINE CHANGE - a zone is a solid that happens to be
// 5cm tall.
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid, TerrainKind } from './types';
import { makeSolid } from './solids';
import { cellKey, greedyMesh } from './voxels';

/** Tall enough for terrainAt's probe, too flat for any sight line. */
const ZONE_HEIGHT = 0.05;

export type ZoneGoing = 'normal' | 'difficult' | 'impassable';

/**
 * How painted ground MOVES on the display (plan map-display, F; the user's ruling of 2026-10-07): burning
 * ground flickers, a hazard's edge crawls, rough going bubbles, ground that gives cover sways, the rest is
 * still. Derived from what the surface does (`surfaceMotion`); the GM's pick on the surface overrides it.
 * Looks only: nothing in the rules reads it.
 */
export type SurfaceMotion = 'flames' | 'crawl' | 'bubbles' | 'sway' | 'still';
/** Each motion's name in the editor: typed, so a new motion without one is a type error. */
export const MOTION_LABELS: Record<SurfaceMotion, string> = {
  flames: 'Flames',
  crawl: 'Crawl',
  bubbles: 'Bubbles',
  sway: 'Sway',
  still: 'Still',
};
export const SURFACE_MOTIONS = Object.keys(MOTION_LABELS) as SurfaceMotion[];

export interface Zone {
  id: string;
  /** Which palette surface painted it - see `zoneFromSurface`. */
  surfaceKey?: string;
  /** Cell keys, "x,z" - a footprint, with no y at all. */
  cells: string[];
  going: ZoneGoing;
  /** Crossing it can hurt you. */
  hazard: boolean;
  colour: string;
  label?: string;
  hidden?: boolean;

  /**
   * Condition codes granted to whoever stands on it - the same strings
   * the condition picker writes, so a surface needs no vocabulary of its
   * own. `Pen:agi=-10` is a test modifier; `Fi` is On Fire, which the
   * turn tracker already resolves as 1d10 E ignoring AP. That is why
   * "burning while you stand in it" needed no damage system.
   *
   * See `surfaceEffects.ts`. Derived from position and never stored on
   * the combatant: standing in mud is a relation, not a property.
   */
  grants?: string[];
  /** Trait flags while standing on it - undying, hardy, fromBeyond. */
  traits?: Record<string, boolean | number>;
  /**
   * Cover the ground itself offers, as a PAIR.
   *
   * A level is required: an AP without one produced a cover of level
   * 'none' that every consumer ignored, so a surface set to give cover
   * gave none at all (v7.66.0). Absent means absent.
   */
  coverAp?: number;
  coverLevel?: 'none' | 'low' | 'half' | 'threeQuarter' | 'full';
  /**
   * Whose feet it catches. A flyer is over it and a burrower under it,
   * so by default neither is touched; a warp rift can say otherwise.
   */
  affects?: { flying?: boolean; hovering?: boolean; burrowing?: boolean };
  /** The GM's pick of how it moves; absent, it is derived (`surfaceMotion`). */
  motion?: SurfaceMotion;
  /** Lit ground, when the scene is lit in areas (engine/battlefield/lighting). Absent is unlit. */
  light?: true;
}

let zoneCounter = 0;
/** Monotonic, never Math.random() - see engine/roster/factories.ts. */
function nextZoneId(): string {
  zoneCounter += 1;
  return `zone-${Date.now().toString(36)}-${zoneCounter.toString(36)}`;
}

/**
 * A surface the GM has defined: the template a painted zone is made from.
 *
 * The palette lives on the terrain, like `voxels.materials`, so a map
 * carries its own surfaces. Painting COPIES these fields onto the zone,
 * which is what `surfaceAt` reads - the zone is the truth, the palette is
 * the stationery.
 */
export interface SurfaceDef {
  key: string;
  label: string;
  colour: string;
  going: ZoneGoing;
  hazard: boolean;
  grants?: string[];
  traits?: Record<string, boolean | number>;
  coverAp?: number;
  coverLevel?: 'none' | 'low' | 'half' | 'threeQuarter' | 'full';
  affects?: { flying?: boolean; hovering?: boolean; burrowing?: boolean };
  motion?: SurfaceMotion;
  light?: true;
}

/** How this ground moves: the GM's pick, else what it does - On Fire first, then a hazard, rough going, cover. */
export function surfaceMotion(
  zone: Pick<Zone, 'motion' | 'grants' | 'hazard' | 'going' | 'coverLevel'>,
): SurfaceMotion {
  if (zone.motion) return zone.motion;
  if (zone.grants?.some((code) => code.split(':')[0] === 'Fi')) return 'flames';
  if (zone.hazard) return 'crawl';
  if (zone.going === 'difficult') return 'bubbles';
  return zone.coverLevel && zone.coverLevel !== 'none' ? 'sway' : 'still';
}

/** The palette a map starts with - the old presets, now editable. */
export function defaultSurfaces(): Record<string, SurfaceDef> {
  const out: Record<string, SurfaceDef> = {};
  for (const p of ZONE_PRESETS) {
    out[p.key] = { key: p.key, label: p.label, colour: p.colour, going: p.going, hazard: p.hazard };
  }
  return out;
}

let surfaceCounter = 0;
/** Monotonic, never Math.random() - see utils/entropy.ts. */
export function nextSurfaceKey(): string {
  surfaceCounter += 1;
  return `surface-${surfaceCounter.toString(36)}`;
}

/** The fields a painted zone takes from its surface. */
export function zoneFromSurface(surface: SurfaceDef, label: string): Partial<Zone> {
  const traits = liveTraits(surface.traits);
  return {
    // WHICH SURFACE THIS GROUND CAME FROM. The link used to be the label
    // alone, which breaks the moment a GM names the area - "the Chapel
    // steps" painted from "Rubble" matched nothing, so every later edit
    // to that surface reached the palette and not the map. Conditions set
    // BEFORE painting worked and traits added after did not, which is a
    // confusing way to discover a broken link (v7.78.0).
    surfaceKey: surface.key,
    going: surface.going,
    hazard: surface.hazard,
    colour: surface.colour,
    label,
    ...(surface.grants?.length ? { grants: [...surface.grants] } : {}),
    ...(traits ? { traits } : {}),
    ...(surface.coverAp ? { coverAp: surface.coverAp } : {}),
    ...(surface.coverLevel && surface.coverLevel !== 'none' ? { coverLevel: surface.coverLevel } : {}),
    ...(surface.affects ? { affects: { ...surface.affects } } : {}),
    ...(surface.motion ? { motion: surface.motion } : {}),
    ...(surface.light ? { light: true as const } : {}),
  };
}

/** The traits actually on: a switched-off one used to linger as `false` (v8.310.8). */
function liveTraits(traits: SurfaceDef['traits']): Record<string, boolean | number> | null {
  const live = Object.fromEntries(Object.entries(traits ?? {}).filter(([, v]) => v));
  return Object.keys(live).length ? live : null;
}

export const ZONE_PRESETS = [
  { key: 'difficult', label: 'Rough going', going: 'difficult' as ZoneGoing, hazard: false, colour: '#a16207' },
  { key: 'hazard', label: 'Hazard', going: 'normal' as ZoneGoing, hazard: true, colour: '#b91c1c' },
  { key: 'mire', label: 'Deep mire', going: 'difficult' as ZoneGoing, hazard: true, colour: '#7c2d12' },
  { key: 'void', label: 'Impassable', going: 'impassable' as ZoneGoing, hazard: false, colour: '#1f2937' },
] as const;

export function makeZone(overrides: Partial<Zone> = {}): Zone {
  return {
    id: nextZoneId(),
    cells: [],
    going: 'difficult',
    hazard: false,
    colour: '#a16207',
    ...overrides,
    ...(overrides.cells ? { cells: [...overrides.cells] } : {}),
  };
}

export const zoneCellKey = (x: number, z: number): string => `${x},${z}`;

export function parseZoneCell(key: string): { x: number; z: number } {
  const [x, z] = key.split(',').map(Number);
  return { x, z };
}

/**
 * What kind of ground this zone is.
 *
 * Impassable outranks everything - you never enter it, so whether it
 * would also have hurt you is moot. Otherwise a hazard outranks rough
 * going, because "this can kill you" is the more urgent of the two and
 * TerrainKind only has room for one.
 */
export function zoneTerrain(zone: Pick<Zone, 'going' | 'hazard'>): TerrainKind {
  if (zone.going === 'impassable') return 'impassable';
  if (zone.hazard) return 'dangerous';
  return zone.going;
}

/** Greedy rectangles over a footprint - the 2D cousin of the voxel mesher. */
function greedyRects(cells: string[]): { x: number; z: number; w: number; d: number }[] {
  // Zone cells as voxels on one layer: the voxel pass sorts by z then x
  // there and grows along x then z, exactly the rectangle pass this was.
  const present = new Set(
    cells.map((key) => {
      const { x, z } = parseZoneCell(key);
      return cellKey(x, 0, z);
    }),
  );
  return greedyMesh(present, (key) => (present.has(key) ? 'zone' : undefined)).map(({ x, z, w, d }) => ({
    x,
    z,
    w,
    d,
  }));
}

const meshCache = new WeakMap<Zone, Solid[]>();

/** One painted rectangle as a solid. */
function zoneSolid(
  zone: Zone,
  rect: { x: number; z: number; w: number; d: number },
  size: number,
  terrain: TerrainKind,
): Solid {
  const motion = surfaceMotion(zone);
  return makeSolid({
    sides: 4,
    radiusBottom: 0.5 * size,
    radiusTop: 0.5 * size,
    scaleX: rect.w,
    scaleZ: rect.d,
    height: ZONE_HEIGHT,
    position: { x: (rect.x + rect.w / 2) * size, y: 0, z: (rect.z + rect.d / 2) * size },
    // Explicitly NO cover, and see-through. A zone marks ground; it is
    // not something to hide behind, and a sight line must pass over it
    // without noticing.
    cover: { level: 'none', ap: 0 },
    coverMode: 'explicit',
    transparency: 2,
    opaque: false,
    // Nor something to stand ON: paint has no thickness worth a token's
    // elevation, a climb charge, or a storey of its own (v8.199.20).
    flat: true,
    colour: zone.colour,
    terrain,
    ...(zone.label ? { label: zone.label } : {}),
    ...(zone.hidden ? { hidden: true } : {}),
    ...(motion !== 'still' ? { motion } : {}),
  });
}

/** One zone as flat solids. Memoised on identity, like everything else here. */
export function meshZone(zone: Zone, size = 1): Solid[] {
  const cached = meshCache.get(zone);
  if (cached) return cached;

  const terrain = zoneTerrain(zone);
  const solids = greedyRects(zone.cells).map((rect) => zoneSolid(zone, rect, size, terrain));

  meshCache.set(zone, solids);
  return solids;
}

/** Every zone on the map as solids. */
export function meshZones(zones: Zone[] | undefined, size = 1): Solid[] {
  if (!zones?.length) return [];
  return zones.flatMap((zone) => meshZone(zone, size));
}

/**
 * A painted zone brought back into line with its surface.
 *
 * Everything the SURFACE owns is re-derived rather than merged, so
 * removing a trait or a cover level actually removes it - a spread would
 * leave the old value behind, since there is no key to overwrite it with.
 * The zone keeps what is its own: its id, its cells, its hiddenness.
 */
export function zoneWithSurface(zone: Zone, surface: SurfaceDef, label: string): Zone {
  const { grants: _g, traits: _t, coverAp: _a, coverLevel: _l, affects: _f, motion: _m, light: _li, ...kept } = zone;
  return { ...kept, ...zoneFromSurface(surface, label) } as Zone;
}

/** Add cells to a zone. Returns a NEW zone. */
export function paintZone(zone: Zone, keys: string[]): Zone {
  const next = new Set(zone.cells);
  let changed = false;
  for (const key of keys) {
    if (!next.has(key)) {
      next.add(key);
      changed = true;
    }
  }
  return changed ? { ...zone, cells: [...next] } : zone;
}

/**
 * A cell holds ONE surface (plan map-display, F; the user's Build ruling): ground that arrives overlapping - a
 * map file or a document from before painting carved (v8.310.8) - keeps the LATER zone in every shared cell,
 * the way painting does. A zone left with no cells is gone. Ground that does not overlap is returned as it
 * came, the same array, so a read changes nothing it does not have to.
 */
export function oneSurfacePerCell<Z extends Pick<Zone, 'cells'>>(zones: Z[] | undefined): Z[] | undefined {
  if (!zones?.length) return zones;
  const taken = new Set<string>();
  // From the last back: each zone keeps only the cells no later zone took.
  const kept = [...zones].reverse().map((zone) => withoutTaken(zone, taken));
  if (kept.every((zone, i) => zone === zones[zones.length - 1 - i])) return zones;
  return kept.filter((zone) => zone.cells.length > 0).reverse();
}

/** This zone without the cells already taken (the same zone when it loses none), and its cells taken. */
function withoutTaken<Z extends Pick<Zone, 'cells'>>(zone: Z, taken: Set<string>): Z {
  // A zone read off a file may have no cell list at all: it covers nothing, and is dropped rather than thrown on.
  const all: string[] = Array.isArray(zone.cells) ? zone.cells : [];
  const clean = all === zone.cells && !all.some((key) => taken.has(key));
  const cells = clean ? all : all.filter((key) => !taken.has(key));
  all.forEach((key) => taken.add(key));
  return clean ? zone : { ...zone, cells };
}

/** Remove cells from a zone. Returns a NEW zone, or the same one. */
export function eraseZone(zone: Zone, keys: string[]): Zone {
  const drop = new Set(keys);
  const cells = zone.cells.filter((key) => !drop.has(key));
  return cells.length === zone.cells.length ? zone : { ...zone, cells };
}
